import type { Page } from "@/api/api.types";

export type ValidatorPhase = "deterministic" | "llm_backed" | "claim_dependent";

export type ValidatorMode = "shadow" | "enforce";

export type ValidatorEffectiveMode = "skipped" | "shadow" | "enforce";

export type ValidatorRunResult = "flagged" | "clean" | "errored";

export type ValidatorRunResultFilter = ValidatorRunResult | "all";

export type ValidatorEnvMode = "off" | "shadow" | "enforce";

export type ValidatorKind = "deterministic" | "llm";

export type ValidatorFindingClass = "hard" | "standard" | "presence";

export type ValidatorParameterKind =
    | "regex"
    | "integer"
    | "mapping"
    | "text"
    | "setting";

export type ValidatorRegexFlagName =
    | "ASCII"
    | "IGNORECASE"
    | "LOCALE"
    | "UNICODE"
    | "MULTILINE"
    | "DOTALL"
    | "VERBOSE"
    | "TEMPLATE"
    | "DEBUG";

export type ValidatorErrorCode =
    | "validator_locked"
    | "validator_config_unavailable"
    | "validator_label_catalog_invalid"
    | "validator_label_unknown"
    | "validator_not_found";

export class ValidatorError extends Error {
    public readonly code: ValidatorErrorCode;

    constructor(code: ValidatorErrorCode) {
        super(code);
        this.name = "ValidatorError";
        this.code = code;
    }
}

export interface IValidatorLabel {
    name: string;
    helper_text: string;
}

export interface IValidatorParameterRead {
    name: string;
    kind: ValidatorParameterKind;
    description: string | null;
    value: string | number;
    regex_flags: number | null;
    regex_flag_names: ValidatorRegexFlagName[];
    source_module: string;
    source_name: string;
    shared_consumers: string[];
}

export interface IValidatorStatus {
    name: string;
    display_name: string;
    description: string;
    kind: ValidatorKind;
    phase: ValidatorPhase;
    finding_classes: ValidatorFindingClass[];
    locked: boolean;
    enabled: boolean;
    mode: ValidatorMode;
    effective_mode: ValidatorEffectiveMode;
    updated_at: string | null;
    labels: IValidatorLabel[];
}

export interface IValidatorDetail extends IValidatorStatus {
    langfuse_eval_name: string | null;
    stage: {
        env_mode: ValidatorEnvMode;
    };
    label_catalog: IValidatorLabel[];
    parameters: IValidatorParameterRead[];
    parameters_fingerprint: string;
}

export interface IValidatorsResponse {
    stage: {
        env_mode: ValidatorEnvMode;
    };
    label_catalog: IValidatorLabel[];
    validators: IValidatorStatus[];
}

export interface IValidatorRunUser {
    id: number;
    email: string;
    display_name: string;
    is_deleted: boolean;
}

export interface IValidatorRunOrganization {
    id: number;
    name: string;
}

export interface IValidatorRunMetadata {
    schema_version: number;
    turn_outcome?:
        | "clean"
        | "repaired_clean"
        | "hard_fallback"
        | "standard_note"
        | "presence_shipped"
        | "fail_open"
        | null;
    repair_requested?: boolean | null;
    detector_status?: "ok" | "error" | "timeout" | "skipped" | null;
    finding_count?: number | null;
    finding_counts_by_class?: Partial<
        Record<ValidatorFindingClass, number>
    > | null;
    findings?:
        | {
              finding_class: ValidatorFindingClass;
              span_length: number;
              detail?: string | null;
              detail_state: "full" | "truncated" | "dropped" | "absent";
          }[]
        | null;
    environment_mode?: ValidatorEnvMode | null;
    policy_fingerprint?: string | null;
    langfuse_trace_id?: string | null;
    langfuse_observation_id?: string | null;
}

export interface IValidatorRunMetadataRead {
    status: "ok" | "unsupported_version" | "unparseable";
    metadata: IValidatorRunMetadata | null;
}

export interface IValidatorRunRead {
    id: number;
    user: IValidatorRunUser | null;
    organization: IValidatorRunOrganization;
    conversation_id: number | null;
    assistant_message_id: number | null;
    validator_name: string;
    result: ValidatorRunResult;
    effective_mode: ValidatorEffectiveMode;
    parameters_fingerprint: string | null;
    metadata: IValidatorRunMetadataRead;
    langfuse_trace_url: string | null;
    created_at: string;
}

export type IValidatorRunsPage = Page<IValidatorRunRead>;

export interface IValidatorPerformanceDay {
    date: string;
    flagged: number;
    clean: number;
    errored: number;
}

export interface IValidatorParameterChange {
    date: string;
    previous: string[];
    current: string[];
}

export interface IValidatorPerformanceRead {
    validator_name: string;
    since: string;
    until: string;
    days: IValidatorPerformanceDay[];
    parameter_changes: IValidatorParameterChange[];
}

export interface IValidatorEvalResult {
    result_id: string;
    timestamp: string;
    value: string;
    url: string;
}

export interface IValidatorEvalResults {
    status: "not_linked" | "linked" | "unavailable";
    evaluator_name: string | null;
    results: IValidatorEvalResult[];
}

interface IValidatorUpdateFields {
    enabled: boolean;
    mode: ValidatorMode;
    label_names: string[];
    label_helper_texts: Record<string, string>;
    langfuse_eval_name: string | null;
}

export type IValidatorUpdate = {
    [Field in keyof IValidatorUpdateFields]: Pick<
        IValidatorUpdateFields,
        Field
    > &
        Partial<Omit<IValidatorUpdateFields, Field>>;
}[keyof IValidatorUpdateFields];
